import clsx from 'clsx';
import React, { useCallback } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { CreateAgentFormData, CreateAgentSchema } from '../../../services/agent/schema/CreateAgentSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateAgent } from '../../../services/agent/hooks/useCreateAgent';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface CreateAgentDialogProps {
    show: boolean;
    onClose: () => void;
}

const CreateAgentDialog: React.FC<CreateAgentDialogProps> = ({ show, onClose }) => {
    const { register, handleSubmit, formState: { errors }, setValue, reset, setError } = useForm<CreateAgentFormData>({
        resolver: zodResolver(CreateAgentSchema),
        defaultValues: {
            color: '#000000',
        }
    });

    const handleClose = () => {
        reset();
        onClose();
    }

    const { mutate, isPending } = useCreateAgent();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: any) => {
        toast.loading('Creating agent...', { id: 'create-agent' });
        mutate(data, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['agents'].includes(key as string))
                });

                toast.success('Agent created successfully', { id: 'create-agent' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof CreateAgentFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create agent', { id: 'create-agent' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to create agent', { id: 'create-agent' });
                }
            }
        });
    }, [mutate]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Agent</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                    <div className="action-sheet-content flex-grow-1">
                        {/* Name */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                    id="name"
                                    placeholder="Name"
                                    {...register('name')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.name && <div className="invalid-feedback">{errors.name.message}</div>}
                            </div>
                        </div>

                        {/* Color */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="color" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>
                                Color:
                            </label>
                            <div className="col-sm-8 d-flex align-items-center">
                                {/* Color Picker */}
                                <input
                                    type="color"
                                    className={clsx("form-control app-form-control rounded-1", errors?.color && 'is-invalid')}
                                    id="color"
                                    {...register('color')}
                                    style={{ width: '45px', height: '35px', padding: 0, border: 'none' }}
                                    onChange={(e) => setValue("color", e.target.value)}
                                />
                                {/* Text Input for Color */}
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1 ms-2", errors?.color && 'is-invalid')}
                                    {...register('color')}
                                    style={{ fontSize: '14px', height: '35px', width: '100px' }}
                                    onChange={(e) => setValue("color", e.target.value)}
                                />
                                {errors?.color && <div className="invalid-feedback ms-2">{errors.color.message}</div>}
                            </div>
                        </div>

                        {/* Website */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="website" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Website: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.website && 'is-invalid')}
                                    id="website"
                                    placeholder="Website"
                                    {...register('website')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.website && <div className="invalid-feedback">{errors.website.message}</div>}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="email" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Email: </label>
                            <div className="col-sm-8">
                                <input
                                    type="email"
                                    className={clsx("form-control app-form-control rounded-1", errors?.email && 'is-invalid')}
                                    id="email"
                                    placeholder="Email"
                                    {...register('email')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.email && <div className="invalid-feedback">{errors.email.message}</div>}
                            </div>
                        </div>

                        {/* Phone */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="phone" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Phone: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.phone && 'is-invalid')}
                                    id="phone"
                                    placeholder="Phone"
                                    {...register('phone')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                            Create
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default CreateAgentDialog